/**
 * Reference-media route manifests (VM-1071).
 *
 * Contracts answer "what can this model accept?". This registry answers the separate question
 * "how does the selected schema/provider spell it?". Callers normalize once to `references`, then
 * materialize only at a model schema or provider boundary.
 */

import {
  EByteDanceImageGenerationModelType,
  EKreaImageGenerationModelType,
  ECreatifyVideoGenerationModelType,
  EHeyGenVideoGenerationModelType,
  GoogleImageGenerationModelType,
  GoogleVideoGenerationModelType,
  HappyHorseVideoGenerationModelType,
  KlingAIVideoGenerationModelType,
  MinimaxVideoGenerationModelType,
  OpenAIImageGenerationModelType,
  SeedanceVideoGenerationModelType,
  SkyReelsVideoGenerationModelType,
  WavespeedVideoGenerationModelType,
} from "../../image/enums"
import { BadDataError } from "../../trpcErrors"
import type { ReferenceMediaModelType } from "./contract"
import { asDisplayString } from "@aix/jsutils/src/coerce"

export const REFERENCE_MEDIA_TYPES = ["image", "video", "audio"] as const
export type ReferenceMediaType = (typeof REFERENCE_MEDIA_TYPES)[number]

export interface ReferenceMediaInput {
  images?: string[]
  videos?: string[]
  audios?: string[]
}

/** Transport safety rails; model contracts still own the tighter capability limits. */
export const REFERENCE_MEDIA_REQUEST_MAX_PER_TYPE = 32
export const REFERENCE_MEDIA_REQUEST_MAX_TOTAL = 64

export type ReferenceMediaProvider = "replicate" | "fal" | "wavespeed" | "kie-ai" | "openrouter"

export interface ReferenceFieldRoute {
  field: string
  cardinality?: "one" | "many"
}

export type ReferenceFieldRoutes = Partial<Record<ReferenceMediaType, ReferenceFieldRoute>>

export interface ReferenceMediaRouteManifest {
  schema: ReferenceFieldRoutes
  /** Compatibility-only input spellings. New callers use `ReferenceMediaInput`. */
  legacy?: Partial<Record<ReferenceMediaType, string[]>>
  /** An omitted provider means that provider does not support references for this model. */
  providers: Partial<Record<ReferenceMediaProvider, ReferenceFieldRoutes>>
  /** Model-schema fields that select first/last-frame mode. */
  frameFields?: string[]
}

const many = (field: string): ReferenceFieldRoute => ({ field, cardinality: "many" })
const one = (field: string): ReferenceFieldRoute => ({ field, cardinality: "one" })

// Motion control: `input_urls` = the character image(s) to animate, `video_urls` = the motion source.
// Without the image route, `references.images` never reached `input_urls` and every CLI/API call
// that used the canonical reference input submitted an empty character list.
const KLING_MOTION_CONTROL_FIELDS: ReferenceFieldRoutes = {
  image: many("input_urls"),
  video: many("video_urls"),
}

const KLING_MOTION_CONTROL_ROUTE: ReferenceMediaRouteManifest = {
  schema: KLING_MOTION_CONTROL_FIELDS,
  legacy: { image: ["input_urls"], video: ["video_urls"] },
  providers: { "kie-ai": KLING_MOTION_CONTROL_FIELDS },
}

const SEEDANCE_URL_FIELDS: ReferenceFieldRoutes = {
  image: many("reference_image_urls"),
  video: many("reference_video_urls"),
  audio: many("reference_audio_urls"),
}

const SEEDANCE_PLURAL_FIELDS: ReferenceFieldRoutes = {
  image: many("reference_images"),
  video: many("reference_videos"),
  audio: many("reference_audios"),
}

const SEEDANCE_FAL_FIELDS: ReferenceFieldRoutes = {
  image: many("image_urls"),
  video: many("video_urls"),
  audio: many("audio_urls"),
}

const SEEDANCE_LEGACY_FIELDS = {
  image: ["reference_image_urls", "reference_images"],
  video: ["reference_video_urls", "reference_videos"],
  audio: ["reference_audio_urls", "reference_audios"],
} satisfies ReferenceMediaRouteManifest["legacy"]

const seedance20Route = (schema: ReferenceFieldRoutes): ReferenceMediaRouteManifest => ({
  schema,
  legacy: SEEDANCE_LEGACY_FIELDS,
  providers: {
    replicate: SEEDANCE_PLURAL_FIELDS,
    fal: SEEDANCE_FAL_FIELDS,
    wavespeed: SEEDANCE_PLURAL_FIELDS,
    "kie-ai": SEEDANCE_URL_FIELDS,
  },
  frameFields: ["first_frame_url", "last_frame_url", "image", "last_frame_image"],
})

const imageInputRoute = (providers: ReferenceMediaRouteManifest["providers"]): ReferenceMediaRouteManifest => ({
  schema: { image: many("image_input") },
  legacy: { image: ["image_input", "reference_images", "image_urls", "image_url"] },
  providers,
})

const WAN_3_I2V_ROUTE: ReferenceMediaRouteManifest = {
  schema: { image: one("start_image") },
  legacy: { image: ["start_image"] },
  providers: {
    replicate: { image: one("image") },
    fal: { image: one("start_image_url") },
  },
  frameFields: ["start_image"],
}

/** Every reference-capable model has exactly one route manifest. */
export const REFERENCE_MEDIA_ROUTES: Partial<Record<ReferenceMediaModelType, ReferenceMediaRouteManifest>> = {
  [EKreaImageGenerationModelType.KREA_3_EDIT]: imageInputRoute({ wavespeed: { image: many("images") } }),
  [OpenAIImageGenerationModelType.GPT_IMAGE_2_EDIT]: {
    schema: { image: many("input_urls") },
    legacy: { image: ["input_urls", "input_images", "image_urls", "image_url"] },
    providers: {
      replicate: { image: many("input_images") },
      fal: { image: many("image_urls") },
      "kie-ai": { image: many("input_urls") },
      openrouter: { image: many("input_urls") },
    },
  },
  [ECreatifyVideoGenerationModelType.BOREAL]: {
    schema: { image: one("image_url"), audio: one("audio_url") },
    legacy: { image: ["image_url"], audio: ["audio_url"] },
    providers: { fal: { image: one("image_url"), audio: one("audio_url") } },
  },
  [GoogleImageGenerationModelType.NANO_BANANA_PRO]: imageInputRoute({
    replicate: { image: many("image_input") },
    fal: { image: many("image_urls") },
    "kie-ai": { image: many("image_input") },
    openrouter: { image: many("image_input") },
  }),
  [GoogleImageGenerationModelType.NANO_BANANA_2_1]: imageInputRoute({
    replicate: { image: many("image_input") },
    "kie-ai": { image: many("image_input") },
    wavespeed: { image: many("images") },
  }),
  [GoogleImageGenerationModelType.NANO_BANANA_2]: imageInputRoute({
    fal: { image: many("image_urls") },
    "kie-ai": { image: many("image_input") },
    openrouter: { image: many("image_input") },
  }),
  [GoogleImageGenerationModelType.GEMINI_2_5_FLASH_IMAGE]: imageInputRoute({
    replicate: { image: many("image_input") },
  }),
  [EByteDanceImageGenerationModelType.SEEDREAM_4_5]: imageInputRoute({
    // FAL exposes this model only as text-to-image. Replicate owns its reference route.
    replicate: { image: many("image_input") },
  }),

  [SeedanceVideoGenerationModelType.SEEDANCE_2]: seedance20Route(SEEDANCE_URL_FIELDS),
  [SeedanceVideoGenerationModelType.SEEDANCE_2_FAST]: seedance20Route(SEEDANCE_URL_FIELDS),
  [SeedanceVideoGenerationModelType.SEEDANCE_2_MINI]: seedance20Route(SEEDANCE_PLURAL_FIELDS),
  [SeedanceVideoGenerationModelType.SEEDANCE_2_5]: {
    ...seedance20Route(SEEDANCE_PLURAL_FIELDS),
    providers: {
      replicate: SEEDANCE_PLURAL_FIELDS,
      fal: SEEDANCE_FAL_FIELDS,
    },
    frameFields: ["image", "last_frame_image"],
  },
  [MinimaxVideoGenerationModelType.H3]: {
    schema: SEEDANCE_URL_FIELDS,
    legacy: SEEDANCE_LEGACY_FIELDS,
    providers: { fal: SEEDANCE_URL_FIELDS },
    frameFields: ["image_url", "end_image_url"],
  },
  [EHeyGenVideoGenerationModelType.HEYGEN_VIDEO]: {
    schema: { image: many("reference_image_urls") },
    legacy: { image: ["reference_image_urls"] },
    providers: { fal: { image: many("reference_image_urls") } },
    frameFields: ["image_url"],
  },
  [GoogleVideoGenerationModelType.VEO_3_1]: {
    schema: { image: many("reference_images") },
    legacy: { image: ["reference_images"] },
    providers: { replicate: { image: many("reference_images") } },
  },
  [GoogleVideoGenerationModelType.GEMINI_OMNI_FLASH_PREVIEW]: {
    schema: { image: many("reference_image_urls") },
    legacy: { image: ["reference_image_urls"] },
    providers: {
      fal: { image: many("image_urls") },
      wavespeed: { image: many("images") },
      "kie-ai": { image: many("image_urls") },
    },
  },
  [GoogleVideoGenerationModelType.GEMINI_OMNI_FLASH_1_1]: {
    schema: { image: many("reference_image_urls"), video: many("reference_video_urls") },
    legacy: { image: ["reference_image_urls", "image_urls"], video: ["reference_video_urls"] },
    providers: {
      fal: { image: many("image_urls"), video: many("reference_video_urls") },
      wavespeed: { image: many("images"), video: many("reference_videos") },
    },
    frameFields: ["image_url", "end_image_url"],
  },
  [KlingAIVideoGenerationModelType.KLING_STANDARD_1_6]: {
    schema: { image: many("reference_images") },
    legacy: { image: ["reference_images"] },
    providers: { replicate: { image: many("reference_images") } },
  },
  [KlingAIVideoGenerationModelType.KLING_PRO_1_6]: {
    schema: { image: many("reference_images") },
    legacy: { image: ["reference_images"] },
    providers: { replicate: { image: many("reference_images") } },
  },
  [KlingAIVideoGenerationModelType.KLING_3_0_MOTION_CONTROL]: KLING_MOTION_CONTROL_ROUTE,
  [KlingAIVideoGenerationModelType.KLING_2_6_MOTION_CONTROL]: KLING_MOTION_CONTROL_ROUTE,
  [HappyHorseVideoGenerationModelType.HAPPYHORSE_R2V]: {
    schema: { image: one("reference_image") },
    legacy: { image: ["reference_image"] },
    providers: { "kie-ai": { image: one("reference_image") } },
  },
  [SkyReelsVideoGenerationModelType.SKYREELS_V4_R2V]: {
    schema: { image: one("reference_image") },
    legacy: { image: ["reference_image"] },
    providers: { wavespeed: { image: one("reference_image") } },
  },
  [WavespeedVideoGenerationModelType.WAN_2_7_R2V]: {
    schema: { image: one("reference_image"), video: one("reference_video") },
    legacy: { image: ["reference_image"], video: ["reference_video"] },
    providers: { "kie-ai": { image: one("reference_image"), video: one("reference_video") } },
  },
  [WavespeedVideoGenerationModelType.WAN_2_7_VIDEOEDIT]: {
    schema: { image: one("reference_image") },
    legacy: { image: ["reference_image"] },
    providers: { "kie-ai": { image: one("reference_image") } },
  },
  [WavespeedVideoGenerationModelType.WAN_3_0_I2V]: WAN_3_I2V_ROUTE,
  [WavespeedVideoGenerationModelType.WAN_3_0_PRIME_I2V]: WAN_3_I2V_ROUTE,
}

const inputKeyByType: Record<ReferenceMediaType, keyof ReferenceMediaInput> = {
  image: "images",
  video: "videos",
  audio: "audios",
}

const readReferenceValues = (value: unknown): string[] => {
  if (typeof value === "string") return value.trim() ? [value] : []
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0)
}

const unique = (values: string[]): string[] => [...new Set(values)]

export const getReferenceMediaRoute = (modelId: string): ReferenceMediaRouteManifest | undefined =>
  REFERENCE_MEDIA_ROUTES[modelId as ReferenceMediaModelType]

/** Normalize the new request shape plus every accepted legacy/schema spelling. */
export const extractReferenceMedia = (
  modelId: string,
  parameters: Record<string, unknown>,
  references?: ReferenceMediaInput,
): ReferenceMediaInput => {
  const route = getReferenceMediaRoute(modelId)
  const embeddedReferences =
    parameters.references && typeof parameters.references === "object" && !Array.isArray(parameters.references)
      ? (parameters.references as ReferenceMediaInput)
      : undefined
  const normalized: ReferenceMediaInput = {}

  for (const type of REFERENCE_MEDIA_TYPES) {
    const inputKey = inputKeyByType[type]
    const fields = unique([
      ...(route?.schema[type]?.field ? [route.schema[type]!.field] : []),
      ...(route?.legacy?.[type] ?? []),
    ])
    const values = unique([
      ...readReferenceValues(references?.[inputKey]),
      ...readReferenceValues(embeddedReferences?.[inputKey]),
      ...fields.flatMap((field) => readReferenceValues(parameters[field])),
    ])
    if (values.length > 0) normalized[inputKey] = values
  }

  return normalized
}

export const countNormalizedReferences = (references: ReferenceMediaInput): number =>
  REFERENCE_MEDIA_TYPES.reduce((total, type) => total + (references[inputKeyByType[type]]?.length ?? 0), 0)

const assertReferenceMediaRequestLimits = (modelId: string, references: ReferenceMediaInput): void => {
  for (const type of REFERENCE_MEDIA_TYPES) {
    const count = references[inputKeyByType[type]]?.length ?? 0
    if (count > REFERENCE_MEDIA_REQUEST_MAX_PER_TYPE) {
      throw BadDataError(
        `${modelId} reference ${type} input exceeds the request limit of ${REFERENCE_MEDIA_REQUEST_MAX_PER_TYPE}; received ${count}.`,
      )
    }
  }

  const total = countNormalizedReferences(references)
  if (total > REFERENCE_MEDIA_REQUEST_MAX_TOTAL) {
    throw BadDataError(
      `${modelId} reference input exceeds the total request limit of ${REFERENCE_MEDIA_REQUEST_MAX_TOTAL}; received ${total}.`,
    )
  }
}

const allRouteFields = (route: ReferenceMediaRouteManifest): string[] =>
  unique(
    REFERENCE_MEDIA_TYPES.flatMap((type) => [
      route.schema[type]?.field,
      ...(route.legacy?.[type] ?? []),
      ...Object.values(route.providers).flatMap((fields) => {
        const field = fields?.[type]?.field
        return field ? [field] : []
      }),
    ]).filter((field): field is string => typeof field === "string"),
  )

/**
 * `references` is our own transport key and every route field is a spelling we own; neither belongs
 * in an outgoing payload. Strip both on every exit path — an empty `references` used to survive the
 * zero-reference early return and 422 the provider on a request with no reference problem (VM-1083).
 */
const stripReferenceParameters = (
  parameters: Record<string, unknown>,
  route: ReferenceMediaRouteManifest | undefined,
): Record<string, unknown> => {
  const next = { ...parameters }
  delete next.references
  for (const field of route ? allRouteFields(route) : []) delete next[field]
  return next
}

const materializeFields = (
  references: ReferenceMediaInput,
  fields: ReferenceFieldRoutes,
  modelId: string,
  targetLabel: string,
): Record<string, unknown> => {
  const output: Record<string, unknown> = {}
  for (const type of REFERENCE_MEDIA_TYPES) {
    const values = references[inputKeyByType[type]] ?? []
    if (values.length === 0) continue
    const fieldRoute = fields[type]
    if (!fieldRoute) {
      throw BadDataError(`${modelId} does not support reference ${type} media on ${targetLabel}.`)
    }
    if (fieldRoute.cardinality === "one" && values.length !== 1) {
      throw BadDataError(
        `${modelId} requires exactly one reference ${type} on ${targetLabel}; received ${values.length}.`,
      )
    }
    output[fieldRoute.field] = fieldRoute.cardinality === "one" ? values[0] : values
  }
  return output
}

/** Materialize normalized references into the selected model's zod-schema dialect. */
export const materializeReferenceMediaForSchema = (
  modelId: string,
  parameters: Record<string, unknown>,
  references?: ReferenceMediaInput,
): Record<string, unknown> => {
  const route = getReferenceMediaRoute(modelId)
  const normalized = extractReferenceMedia(modelId, parameters, references)
  const next = stripReferenceParameters(parameters, route)
  if (countNormalizedReferences(normalized) === 0) return next
  assertReferenceMediaRequestLimits(modelId, normalized)
  if (!route) throw BadDataError(`${modelId} does not declare reference-media support.`)

  return { ...next, ...materializeFields(normalized, route.schema, modelId, "its model schema") }
}

/** Translate schema/legacy spellings into the selected provider's payload dialect. */
export const materializeReferenceMediaForProvider = (
  parameters: Record<string, unknown>,
  provider: ReferenceMediaProvider,
): Record<string, unknown> => {
  const modelId = asDisplayString(parameters.modelId)
  const route = getReferenceMediaRoute(modelId)
  const normalized = extractReferenceMedia(modelId, parameters)
  // A model with no manifest that still carries references is a caller bug; pass it through so the
  // provider rejects it loudly rather than silently dropping media the user asked for (VM-1082).
  if (countNormalizedReferences(normalized) === 0) return stripReferenceParameters(parameters, route)
  if (!route) return { ...parameters }
  assertReferenceMediaRequestLimits(modelId, normalized)
  const providerFields = route.providers[provider]
  if (!providerFields) throw BadDataError(`${modelId} does not support reference media on ${provider}.`)

  const next = stripReferenceParameters(parameters, route)
  return { ...next, ...materializeFields(normalized, providerFields, modelId, provider) }
}

/**
 * Same as `materializeReferenceMediaForProvider`, but for the model-specific builders that know
 * exactly which model they serve. Those builders used to substitute a default modelId (Seedance 2.0
 * / Gemini Omni) when the caller omitted one, which routed the references through the WRONG
 * manifest: the provider ignored the field names it did not recognize, the job succeeded, the user
 * was billed, and the reference silently vanished — no error, no Sentry event. A missing modelId is
 * a caller bug, so fail closed instead of guessing (VM-1082).
 */
export const materializeReferenceMediaForKnownModel = (
  parameters: Record<string, unknown>,
  provider: ReferenceMediaProvider,
): Record<string, unknown> => {
  if (typeof parameters.modelId !== "string" || !parameters.modelId) {
    throw BadDataError(`Cannot route reference media on ${provider} without a modelId.`)
  }
  return materializeReferenceMediaForProvider(parameters, provider)
}

export const providerSupportsReferenceMedia = (
  modelId: string,
  provider: string,
  parameters: Record<string, unknown>,
  references?: ReferenceMediaInput,
): boolean => {
  const normalized = extractReferenceMedia(modelId, parameters, references)
  if (countNormalizedReferences(normalized) === 0) return true
  return getReferenceMediaRoute(modelId)?.providers[provider as ReferenceMediaProvider] !== undefined
}

export const getReferenceSchemaField = (modelId: string, type: ReferenceMediaType): string | undefined =>
  getReferenceMediaRoute(modelId)?.schema[type]?.field

export const getReferenceFrameFields = (modelId: string): string[] => getReferenceMediaRoute(modelId)?.frameFields ?? []
